import { supabase } from '@/integrations/supabase/client';
import { Proposta, CreatePropostaDTO, UpdatePropostaDTO, PropostaFilters } from '@/features/propostas/types';

const mapearProposta = (proposta: any): Proposta => {
  return {
    id: proposta.id.toString(),
    titulo: proposta.titulo || '',
    descricao: proposta.descricao || '',
    valor: parseFloat(proposta.valor) || 0,
    status: proposta.status || 'PENDENTE',
    data_criacao: new Date(proposta.created_at),
    data_atualizacao: new Date(proposta.updated_at || proposta.created_at),
    cliente_id: proposta.cliente_id?.toString() || '',
    consultor_id: proposta.consultor_id?.toString() || '',
    cliente: proposta.clientes ? {
      id: proposta.clientes.id.toString(),
      nome: proposta.clientes.nome || '',
      email: proposta.clientes.email || '',
      telefone: proposta.clientes.telefone || '',
      cpf: proposta.clientes.cpf || '',
      endereco: proposta.clientes.endereco || '',
      cidade: proposta.clientes.cidade || '',
      estado: proposta.clientes.estado || '',
      cep: proposta.clientes.cep || '',
      data_nascimento: proposta.clientes.data_nascimento ? new Date(proposta.clientes.data_nascimento) : undefined,
      created_at: new Date(proposta.clientes.created_at),
      updated_at: new Date(proposta.clientes.updated_at || proposta.clientes.created_at),
      tenant_id: proposta.clientes.tenant_id?.toString() || '',
    } : undefined,
    consultor: proposta.consultores ? {
      id: proposta.consultores.id.toString(),
      nome: proposta.consultores.nome || '',
      email: proposta.consultores.email || '',
      codigo: proposta.consultores.codigo || '',
      tipo: proposta.consultores.tipo || 'relacionador',
      status: proposta.consultores.status || 'ativo',
      supervisor_id: proposta.consultores.supervisor_id?.toString() || null,
      lider_id: proposta.consultores.lider_id?.toString() || null,
      tenant_id: proposta.consultores.tenant_id?.toString() || '',
      created_at: new Date(proposta.consultores.created_at),
      updated_at: new Date(proposta.consultores.updated_at || proposta.consultores.created_at),
    } : undefined,
    observacoes: proposta.observacoes || '',
    condicoes_comerciais: proposta.condicoes_comerciais || '',
    prazo_entrega: proposta.prazo_entrega || '',
    validade_proposta: proposta.validade_proposta ? new Date(proposta.validade_proposta) : undefined,
    forma_pagamento: proposta.forma_pagamento || '',
    anexos: proposta.anexos || [],
  };
};

export class PropostasSupabaseService {
  /**
   * Lista propostas com filtros e paginação
   */
  async listarPropostas(filtros?: PropostaFilters, tenantId?: string): Promise<Proposta[]> {
    try {
      let query = supabase
        .from('propostas')
        .select(`
          *,
          clientes!inner(*),
          consultores!inner(*)
        `)
        .order('created_at', { ascending: false });

      // Aplicar filtro de tenant
      if (tenantId) {
        query = query.eq('tenant_id', parseInt(tenantId));
      }

      // Aplicar filtros
      if (filtros?.status) {
        query = query.eq('status', filtros.status);
      }
      if (filtros?.cliente_id) {
        query = query.eq('cliente_id', parseInt(filtros.cliente_id));
      }
      if (filtros?.consultor_id) {
        query = query.eq('consultor_id', parseInt(filtros.consultor_id));
      }
      if (filtros?.data_inicio) {
        query = query.gte('created_at', filtros.data_inicio.toISOString());
      }
      if (filtros?.data_fim) {
        query = query.lte('created_at', filtros.data_fim.toISOString());
      }
      if (filtros?.search) {
        query = query.or(`titulo.ilike.%${filtros.search}%,descricao.ilike.%${filtros.search}%`);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Erro ao listar propostas:', error);
        throw error;
      }

      return data?.map(mapearProposta) || [];
    } catch (error) {
      console.error('Erro ao listar propostas:', error);
      throw error;
    }
  }

  /**
   * Busca uma proposta específica
   */
  async buscarProposta(id: string): Promise<Proposta> {
    try {
      const { data, error } = await supabase
        .from('propostas')
        .select(`
          *,
          clientes!inner(*),
          consultores!inner(*)
        `)
        .eq('id', parseInt(id))
        .single();

      if (error) {
        console.error('Erro ao buscar proposta:', error);
        throw error;
      }

      return mapearProposta(data);
    } catch (error) {
      console.error('Erro ao buscar proposta:', error);
      throw error;
    }
  }

  /**
   * Cria uma nova proposta
   */
  async criarProposta(propostaData: CreatePropostaDTO): Promise<Proposta> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuário não autenticado');

      // Buscar o perfil do usuário para pegar o tenant_id
      const { data: userProfile, error: profileError } = await supabase
        .from('profiles')
        .select('tenant_id')
        .eq('id', user.id)
        .single();

      if (profileError || !userProfile) {
        console.error('Erro ao buscar perfil:', profileError);
        throw new Error('Perfil não encontrado para o usuário autenticado');
      }

      const tenantId = userProfile.tenant_id;

      const { data, error } = await supabase
        .from('propostas')
        .insert({
          titulo: propostaData.titulo,
          descricao: propostaData.descricao,
          valor: propostaData.valor,
          cliente_id: parseInt(propostaData.cliente_id),
          consultor_id: parseInt(propostaData.consultor_id || '1'), // TODO: Pegar do contexto do usuário
          tenant_id: tenantId,
          observacoes: propostaData.observacoes,
          condicoes_comerciais: propostaData.condicoes_comerciais,
          prazo_entrega: propostaData.prazo_entrega,
          validade_proposta: propostaData.validade_proposta?.toISOString(),
          forma_pagamento: propostaData.forma_pagamento,
        })
        .select(`
          *,
          clientes!inner(*),
          consultores!inner(*)
        `)
        .single();

      if (error) {
        console.error('Erro ao criar proposta:', error);
        throw error;
      }

      return mapearProposta(data);
    } catch (error) {
      console.error('Erro ao criar proposta:', error);
      throw error;
    }
  }

  /**
   * Atualiza uma proposta existente
   */
  async atualizarProposta(id: string, propostaData: UpdatePropostaDTO): Promise<Proposta> {
    try {
      const updateData: any = {};

      if (propostaData.titulo !== undefined) updateData.titulo = propostaData.titulo;
      if (propostaData.descricao !== undefined) updateData.descricao = propostaData.descricao;
      if (propostaData.valor !== undefined) updateData.valor = propostaData.valor;
      if (propostaData.cliente_id !== undefined) updateData.cliente_id = parseInt(propostaData.cliente_id);
      if (propostaData.status !== undefined) updateData.status = propostaData.status;
      if (propostaData.observacoes !== undefined) updateData.observacoes = propostaData.observacoes;
      if (propostaData.condicoes_comerciais !== undefined) updateData.condicoes_comerciais = propostaData.condicoes_comerciais;
      if (propostaData.prazo_entrega !== undefined) updateData.prazo_entrega = propostaData.prazo_entrega;
      if (propostaData.validade_proposta !== undefined) updateData.validade_proposta = propostaData.validade_proposta?.toISOString();
      if (propostaData.forma_pagamento !== undefined) updateData.forma_pagamento = propostaData.forma_pagamento;

      updateData.updated_at = new Date().toISOString();

      const { data, error } = await supabase
        .from('propostas')
        .update(updateData)
        .eq('id', parseInt(id))
        .select(`
          *,
          clientes!inner(*),
          consultores!inner(*)
        `)
        .single();

      if (error) {
        console.error('Erro ao atualizar proposta:', error);
        throw error;
      }

      return mapearProposta(data);
    } catch (error) {
      console.error('Erro ao atualizar proposta:', error);
      throw error;
    }
  }

  /**
   * Exclui uma proposta
   */
  async excluirProposta(id: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('propostas')
        .delete()
        .eq('id', parseInt(id));

      if (error) {
        console.error('Erro ao excluir proposta:', error);
        throw error;
      }
    } catch (error) {
      console.error('Erro ao excluir proposta:', error);
      throw error;
    }
  }

  /**
   * Aprova uma proposta
   */
  async aprovarProposta(id: string): Promise<Proposta> {
    try {
      const { data, error } = await supabase
        .from('propostas')
        .update({
          status: 'APROVADA',
          updated_at: new Date().toISOString()
        })
        .eq('id', parseInt(id))
        .select(`
          *,
          clientes!inner(*),
          consultores!inner(*)
        `)
        .single();

      if (error) {
        console.error('Erro ao aprovar proposta:', error);
        throw error;
      }

      return mapearProposta(data);
    } catch (error) {
      console.error('Erro ao aprovar proposta:', error);
      throw error;
    }
  }

  /**
   * Rejeita uma proposta
   */
  async rejeitarProposta(id: string): Promise<Proposta> {
    try {
      const { data, error } = await supabase
        .from('propostas')
        .update({
          status: 'REJEITADA',
          updated_at: new Date().toISOString()
        })
        .eq('id', parseInt(id))
        .select(`
          *,
          clientes!inner(*),
          consultores!inner(*)
        `)
        .single();

      if (error) {
        console.error('Erro ao rejeitar proposta:', error);
        throw error;
      }

      return mapearProposta(data);
    } catch (error) {
      console.error('Erro ao rejeitar proposta:', error);
      throw error;
    }
  }

  /**
   * Atualiza status da proposta
   */
  async atualizarStatus(id: string, status: Proposta['status']): Promise<Proposta> {
    try {
      const { data, error } = await supabase
        .from('propostas')
        .update({
          status,
          updated_at: new Date().toISOString()
        })
        .eq('id', parseInt(id))
        .select(`
          *,
          clientes!inner(*),
          consultores!inner(*)
        `)
        .single();

      if (error) {
        console.error('Erro ao atualizar status:', error);
        throw error;
      }

      return mapearProposta(data);
    } catch (error) {
      console.error('Erro ao atualizar status:', error);
      throw error;
    }
  }

  /**
   * Upload de anexos (implementação básica - sem storage)
   */
  async uploadAnexos(id: string, files: FileList): Promise<Proposta> {
    try {
      // Por enquanto, apenas retorna a proposta sem anexos
      // TODO: Implementar upload para Supabase Storage
      console.log('Upload de anexos não implementado ainda');
      return await this.buscarProposta(id);
    } catch (error) {
      console.error('Erro ao fazer upload de anexos:', error);
      throw error;
    }
  }

  /**
   * Remove anexo (implementação básica)
   */
  async removerAnexo(propostaId: string, anexoId: string): Promise<void> {
    try {
      // Por enquanto, apenas log
      // TODO: Implementar remoção de anexos do Supabase Storage
      console.log('Remoção de anexo não implementada ainda');
    } catch (error) {
      console.error('Erro ao remover anexo:', error);
      throw error;
    }
  }
}


